import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const STAGES = [
  { key: 'body', at: 0.04 },
  { key: 'sleeves', at: 0.18 },
  { key: 'collar', at: 0.32 },
  { key: 'stitches', at: 0.48 },
  { key: 'zipper', at: 0.64 },
  { key: 'snaps', at: 0.8 },
  { key: 'glow', at: 0.94 },
];

export const JacketProgress = () => {
  const groupRefs = useRef({});
  const pctRef = useRef(null);
  const wrapRef = useRef(null);
  const svgRef = useRef(null);

  useEffect(() => {
    const st = ScrollTrigger.create({
      trigger: document.body,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        const p = self.progress;
        if (pctRef.current) pctRef.current.textContent = `${Math.round(p * 100)}%`;
        
        if (svgRef.current) {
          const rotY = p * 720; // 2 full spins down the page
          const rotX = Math.sin(p * Math.PI * 6) * 20; 
          svgRef.current.style.transform = `perspective(500px) rotateY(${rotY}deg) rotateX(${rotX}deg)`;
        }

        STAGES.forEach(({ key, at }) => {
          const el = groupRefs.current[key];
          if (!el) return;
          const on = p >= at;
          el.style.opacity = on ? '1' : key === 'glow' ? '0' : '0.12';
          el.style.transform = on ? 'translateY(0)' : 'translateY(3px)';
        });
      },
    });
    return () => st.kill();
  }, []);

  const setRef = (key) => (el) => { groupRefs.current[key] = el; };
  const gStyle = { opacity: 0.12, transition: 'opacity 0.6s ease, transform 0.6s ease' };

  return (
    <div
      ref={wrapRef}
      data-testid="jacket-progress"
      className="fixed bottom-5 left-5 md:bottom-8 md:left-8 z-[70] hidden md:flex flex-col items-center gap-2 pointer-events-none"
    >
      <svg ref={svgRef} width="64" height="76" viewBox="0 0 120 140" fill="none" style={{ willChange: 'transform' }}>
        <g ref={setRef('glow')} style={{ opacity: 0, transition: 'opacity 0.8s ease' }}>
          <path d="M30 36 L48 24 L72 24 L90 36 L96 122 L24 122 Z" fill="#8C5A35" fillOpacity="0.35" />
        </g>
        <g ref={setRef('body')} style={gStyle}>
          <path d="M30 36 L48 24 L72 24 L90 36 L96 122 L24 122 Z" stroke="#B89B72" strokeWidth="2" />
        </g>
        <g ref={setRef('sleeves')} style={gStyle}>
          <path d="M30 36 L12 46 L6 104 L22 100 L27 62" stroke="#B89B72" strokeWidth="2" />
          <path d="M90 36 L108 46 L114 104 L98 100 L93 62" stroke="#B89B72" strokeWidth="2" />
        </g>
        <g ref={setRef('collar')} style={gStyle}>
          <path d="M48 24 L60 36 L72 24 L78 32 L60 46 L42 32 Z" stroke="#B89B72" strokeWidth="2" />
        </g>
        <g ref={setRef('stitches')} style={gStyle}>
          <path d="M38 42 V118" stroke="#F5F5F0" strokeWidth="1.2" strokeDasharray="3 5" />
          <path d="M82 42 V118" stroke="#F5F5F0" strokeWidth="1.2" strokeDasharray="3 5" />
          <path d="M32 40 L46 30" stroke="#F5F5F0" strokeWidth="1.2" strokeDasharray="3 5" />
          <path d="M88 40 L74 30" stroke="#F5F5F0" strokeWidth="1.2" strokeDasharray="3 5" />
        </g>
        <g ref={setRef('zipper')} style={gStyle}>
          <path d="M60 46 V122" stroke="#A3A3A3" strokeWidth="2.4" strokeDasharray="2 2" />
          <circle cx="60" cy="52" r="3" fill="#A3A3A3" />
        </g>
        <g ref={setRef('snaps')} style={gStyle}>
          <circle cx="46" cy="30" r="2.5" fill="#B89B72" />
          <circle cx="74" cy="30" r="2.5" fill="#B89B72" />
          <circle cx="52" cy="116" r="2" fill="#B89B72" />
          <circle cx="68" cy="116" r="2" fill="#B89B72" />
        </g>
      </svg>
      <span ref={pctRef} className="font-accent text-[10px] text-[#B89B72]">0%</span>
      <span className="font-accent text-[8px] text-[#525252] hidden md:block">Assembly</span>
    </div>
  );
};

export default JacketProgress;
